/**
 * Helpers for the table's pinned header and the paginator's pinned footer:
 * which element scrolls them, when they are stuck (from IntersectionObserver
 * entries, never from scroll events), and the scroll padding that keeps a
 * focused row clear of whatever is stuck over it.
 */

/** Overflow values that make an element a scroll container (and so the box a sticky element sticks to). */
const SCROLLING = /^(auto|scroll|hidden|overlay)$/;

/** The element's parent in the flat tree: its parent element, or the host of the shadow root it sits in. */
function parentOf(el: Element): Element | null {
  if (el.parentElement) return el.parentElement;
  const root = el.getRootNode();
  return root instanceof ShadowRoot ? root.host : null;
}

/**
 * The nearest ancestor (crossing shadow roots) that is a scroll container, or
 * null when the document itself scrolls.
 */
export function findScrollContainer(el: Element): HTMLElement | null {
  const doc = el.ownerDocument;
  for (let n = parentOf(el); n; n = parentOf(n)) {
    if (n === doc.body || n === doc.documentElement) return null;
    const style = getComputedStyle(n);
    if (SCROLLING.test(style.overflowY) || SCROLLING.test(style.overflowX)) return n as HTMLElement;
  }
  return null;
}

/**
 * How far past the scroll container's padding a sticky edge must sit to meet
 * the visible edge: sticky offsets count from inside the padding, so a padded
 * scroller would leave a strip of scrolling rows above a pinned header or
 * below a pinned footer.
 */
export function stickyInset(scroller: HTMLElement | null, side: 'top' | 'bottom'): number {
  if (!scroller) return 0;
  const style = getComputedStyle(scroller);
  return -(parseFloat(side === 'top' ? style.paddingTop : style.paddingBottom) || 0);
}

type Rect = Pick<DOMRectReadOnly, 'top' | 'bottom'>;
interface StickyEntry {
  isIntersecting: boolean;
  intersectionRatio: number;
  boundingClientRect: Rect;
  rootBounds: Rect | null;
}

/**
 * Header: a zero-height sentinel sits right above the table, observed with a
 * root that reaches far below the visible area (so only "passed the top"
 * reads as outside). The header is stuck while the sentinel has passed the
 * top of the scroll area and the table is still in view.
 */
export function sentinelPassedTop(entry: StickyEntry): boolean {
  if (entry.isIntersecting || !entry.rootBounds) return false;
  return entry.boundingClientRect.bottom <= entry.rootBounds.top + 0.5;
}

/**
 * Footer: observed with the root's bottom edge pulled in by 1px and
 * thresholds of 0 and 1. Resting in the flow it is fully inside; stuck to the
 * bottom edge, its last pixel is outside. (The ratio decides, not
 * isIntersecting, which browsers report false below the highest threshold.)
 */
export function stuckToBottom(entry: StickyEntry): boolean {
  if (entry.intersectionRatio <= 0 || entry.intersectionRatio >= 1 || !entry.rootBounds) return false;
  return entry.boundingClientRect.bottom > entry.rootBounds.bottom;
}

type Side = 'top' | 'bottom';
const paddings = new WeakMap<HTMLElement, Map<Side, Map<object, number>>>();

/**
 * Asks for scroll padding on one side of a scroll container (null withdraws
 * the request). Several tables and paginators can share one container: the
 * largest request wins, and with none left the container's own value returns.
 */
export function requestScrollPadding(scroller: HTMLElement, side: Side, owner: object, px: number | null): void {
  let sides = paddings.get(scroller);
  if (!sides) paddings.set(scroller, (sides = new Map()));
  let owners = sides.get(side);
  if (!owners) sides.set(side, (owners = new Map()));
  if (px === null) owners.delete(owner);
  else owners.set(owner, px);
  const max = Math.max(0, ...owners.values());
  const prop = side === 'top' ? 'scroll-padding-top' : 'scroll-padding-bottom';
  if (owners.size && max > 0) scroller.style.setProperty(prop, `${Math.ceil(max)}px`);
  else scroller.style.removeProperty(prop);
}
